<template>
  <div id="feedback-content" :style="{ backgroundColor: campaign.feedbackBackgroud }">
    <div class="feedback_page">
      <b-alert v-if="hasErrors" show variant='danger'>
        An unexpected error has occurred. Please try again later or contact support.
      </b-alert>

      <div :class="['location', { 'flex-column text-center': feedbackLogoIsHorizontal }]">
        <img v-if="isShowLogo()" :class="{ horizontal: feedbackLogoIsHorizontal }" :src="campaign.feedbackLogoUrl" alt="logo">
        <i v-else class="fa fa-map-marker-alt" />
        <div class="">
          <div class="name">
            {{ location.name }}
          </div>
          <div v-if="location.address" class="address">
            {{ location.address }}
          </div>
        </div>
      </div>

      <FinishPageComponent v-if="isFeedbackReceived || !campaign.feedbackGatheringEnabled"
        :show-links='feedback.score >= campaign.positiveFeedbackThreshold || !campaign.feedbackGatheringEnabled'
        :links='links'
        :campaign='campaign'
        :contact-id="contactId"
        :rendered-message-id="renderedMessageId"
        :root-domain="rootDomain"
        :feedback="feedback" />

      <form v-else @submit.prevent="onSubmit">
        <CustomFormInput
          v-for="item in formItems"
          :key="item.key"
          :item="item"
          :errors="errors[`feedback_form_values[${item.title}]`]"
          :video-errors="videoErrors"
          :is-processing="isProcessing"
          :is-locked="isLocked"
          :feedback="feedback"
          :feedback-gathering-enabled="campaign.feedbackGatheringEnabled"
          @on-change-item="onChangeCustomInput"
          @on-lock="handleLockState" />
      </form>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { AlertPlugin } from 'bootstrap-vue'
import axios from 'axios'
import CustomFormInput from './feedback_page_widget/custom_form_input'
import FinishPageComponent from './finish_page_component'

Vue.use(AlertPlugin)
function buildFeedback() {
  return {
    id: this.leftFeedback?.id,
    campaign_id: this.campaign.id,
    score: this.leftFeedback?.score,
    body: this.leftFeedback?.body,
    contact_id: this.contactId,
    video_url: null,
    customFormValues: (this.leftFeedback?.feedbackFormValues || this.campaign.feedbackFormItems).reduce((memo, el) => {
      memo[el.title] = el.value || ''
      return memo
    }, {})
  }
}
export default {
  name: 'FeedbackPagesWidget',
  components: {
    CustomFormInput,
    FinishPageComponent
  },
  props: {
    location: Object,
    rootDomain: String,
    links: Array,
    leftFeedback: Object,
    contactId: { type: Number, default: 0 },
    renderedMessageId: { type: String, default: '' },
    campaign: { type: Object, required: true }
  },
  data() {
    return {
      feedback: buildFeedback.call(this),
      isFeedbackReceived: this.leftFeedback !== null,
      hasErrors: false,
      isProcessing: false,
      isLocked: false,
      errors: {},
      videoErrors: [],
      feedbackLogoIsHorizontal: this.campaign.feedbackLogoIsHorizontal
    }
  },
  computed: {
    formItems() {
      if (this.contactId) return this.campaign.feedbackFormItems.filter((el) => el.type !== 'no_contact')

      return this.campaign.feedbackFormItems
    }
  },
  methods: {
    isShowLogo() {
      const img = new Image()
      img.onload = () => {
        this.feedbackLogoIsHorizontal = img.width / img.height >= 2.0
      }
      img.src = this.campaign.feedbackLogoUrl

      return this.campaign.feedbackLogoUrl && this.campaign.feedbackLogoUrl.length > 0
    },
    sendFormWithoutVideo() {
      const formData = new FormData()
      formData.append('rendered_message_id', this.renderedMessageId)
      const { customFormValues, ...feedback } = this.feedback
      Object.keys(feedback).forEach((key) => {
        if (this.feedback[key] != null) {
          formData.append(`feedback[${key}]`, this.feedback[key])
        }
      })
      if (this.campaign.feedbackFormItems.length) {
        this.campaign.feedbackFormItems.forEach((item) => {
          if (['string', 'text', 'dropdown'].includes(item.type)) {
            formData.append(`feedback[feedback_form_values][${item.key}]`, customFormValues[item.key] || '')
          }
        })
      }

      this.errors = {}

      axios.post(`${this.rootDomain}/ajax/feedbacks`, formData)
        .then((res) => {
          this.isFeedbackReceived = true
          this.hasErrors = false
          this.isProcessing = false
          this.feedback.id = res.data.id
        })
        .catch((xhr) => {
          this.isProcessing = false
          if (xhr.response.status === 422) {
            this.errors = xhr.response.data.errors
            this.$nextTick(() => {
              document.querySelector('small.text-danger')?.scrollIntoView({ behavior: 'smooth' })
            })
          } else {
            this.hasErrors = true
          }
        })
    },
    onSubmit() {
      this.isProcessing = true
      const { customFormValues } = this.feedback

      if (customFormValues.video) {
        let filename = ''
        if (customFormValues.video) {
          filename = `${(new Date()).getTime()}`
          if (customFormValues.video.name) {
            const splittedName = customFormValues.video.name.split('.')
            filename += `.${splittedName[splittedName.length - 1]}`
          } else {
            filename += '.webm'
          }
        }

        axios.get('/ajax/feedbacks/upload_url', {
          params: {
            filename: filename,
            'feedback[campaign_id]': this.campaign.id
          }
        })
          .then((res) => {
            const uploadUrl = res.data.upload_url

            const options = {
              headers: {
                'Content-Type': 'multipart/form-data',
                Accept: 'application/json'
              }
            }

            axios.put(uploadUrl, customFormValues.video, options)
              .then(() => {
                this.feedback.video_url = res.data.upload_file_path
                this.sendFormWithoutVideo()
              })
              .catch(() => {
                this.isProcessing = false
                this.videoErrors = ['File upload error']
              })
          })
          .catch(() => {
            this.isProcessing = false
            this.videoErrors = ['File upload error']
          })
      } else {
        this.sendFormWithoutVideo()
      }
    },
    onChangeCustomInput({ key, val }) {
      if (['score', 'body', 'email', 'phone'].includes(key)) {
        this.onChangeInput({ key, val })
        return
      }
      this.feedback.customFormValues[key] = val
    },
    onChangeInput({ key, val }) {
      this.feedback[key] = val
    },
    handleLockState(state) {
      this.isLocked = state
    }
  },
  watch: {
    leftFeedback(val) {
      this.isFeedbackReceived = val !== null
      this.feedback = buildFeedback.call(this)
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~stylesheets/feedback_page/variables';
@import '~stylesheets/feedback_page/form';
@import '~stylesheets/feedback_page/page';
</style>
